import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pause, Play, Trash2, Pencil } from "lucide-react";
import { useStore } from "@/lib/storeContext";

const STORE_PALETTE = [
  { hex: "#EF4444", label: "Rosso" },
  { hex: "#EAB308", label: "Giallo" },
  { hex: "#F97316", label: "Arancio" },
  { hex: "#22C55E", label: "Verde" },
  { hex: "#3B82F6", label: "Blu" },
  { hex: "#8B5CF6", label: "Viola" },
  { hex: "#EC4899", label: "Rosa" },
  { hex: "#1E2D5B", label: "Blu notte" },
];

function ColorDots({ value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {STORE_PALETTE.map((c) => (
        <button
          key={c.hex}
          type="button"
          title={c.label}
          disabled={disabled}
          data-testid={`store-color-${c.hex.replace("#", "")}`}
          onClick={() => onChange(c.hex)}
          className={`w-8 h-8 rounded-full border-2 ${value === c.hex ? "border-[#18181B] scale-110" : "border-transparent"}`}
          style={{ backgroundColor: c.hex }}
        />
      ))}
    </div>
  );
}

function CardPosEditor({ store, owner, onSaved }) {
  const [enabled, setEnabled] = useState(!!store.card_pos_enabled);
  const [host, setHost] = useState(store.card_pos_host || "");
  const [port, setPort] = useState(String(store.card_pos_port || 1000));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!store.card_pos_enabled);
    setHost(store.card_pos_host || "");
    setPort(String(store.card_pos_port || 1000));
  }, [store.id, store.card_pos_enabled, store.card_pos_host, store.card_pos_port]);

  const save = async () => {
    if (!owner) return;
    const portN = parseInt(port, 10);
    if (enabled && !String(host).trim()) {
      toast.error("Inserisci l'IP del POS, oppure spegni l'interruttore");
      return;
    }
    if (enabled && (!portN || portN < 1 || portN > 65535)) {
      toast.error("Porta POS non valida");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/stores/${store.id}`, {
        card_pos_enabled: enabled,
        card_pos_host: String(host).trim(),
        card_pos_port: portN || 1000,
        card_pos_protocol: "17",
      });
      toast.success(enabled ? "POS elettronico salvato per questa sede" : "POS elettronico disattivato: Carta resta manuale");
      onSaved(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile salvare il POS"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-velta-border space-y-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">POS pagamenti (protocollo 17)</div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid={`store-pos-enabled-${store.id}`}
          checked={enabled}
          disabled={!owner}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Collega il POS di questa sede (IP fisso in LAN)
      </label>
      {enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="sm:col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">IP</label>
            <input
              data-testid={`store-pos-host-${store.id}`}
              value={host}
              disabled={!owner}
              onChange={(e) => setHost(e.target.value)}
              placeholder="192.168.1.50"
              className="h-11 w-full border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Porta</label>
            <input
              data-testid={`store-pos-port-${store.id}`}
              value={port}
              disabled={!owner}
              onChange={(e) => setPort(e.target.value)}
              placeholder="1000"
              className="h-11 w-full border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
            />
          </div>
        </div>
      )}
      {owner && (
        <button
          type="button"
          data-testid={`store-pos-save-${store.id}`}
          disabled={saving}
          onClick={save}
          className="h-10 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md text-sm font-medium disabled:opacity-40"
        >
          Salva POS sede
        </button>
      )}
    </div>
  );
}

function StoreIdentity({ store, open, onClose, onSaved }) {
  const [name, setName] = useState(store.name || "");
  const [address, setAddress] = useState(store.address || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(store.name || "");
    setAddress(store.address || "");
  }, [store.id, store.name, store.address, open]);

  if (!open) return null;

  const save = async () => {
    if (!name.trim()) return toast.error("Il nome è obbligatorio");
    setSaving(true);
    try {
      const r = await api.patch(`/stores/${store.id}`, { name: name.trim(), address: address.trim() });
      toast.success("Punto vendita aggiornato");
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile aggiornare il punto vendita"));
    } finally {
      setSaving(false);
    }
  };

  const makeMain = async () => {
    setSaving(true);
    try {
      await api.patch(`/stores/${store.id}`, { is_main: true });
      toast.success("Sede principale aggiornata");
      onSaved(null);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile impostare la sede principale"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`store-edit-panel-${store.id}`}>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Nome</label>
        <input data-testid={`store-edit-name-${store.id}`} value={name} onChange={(e) => setName(e.target.value)} className="h-11 w-full border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Indirizzo</label>
        <input data-testid={`store-edit-address-${store.id}`} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Indirizzo" className="h-11 w-full border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
      </div>
      <div className="sm:col-span-2 flex flex-wrap gap-2">
        <button type="button" data-testid={`store-save-${store.id}`} disabled={saving} onClick={save} className="h-10 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md text-sm font-medium disabled:opacity-40">Salva sede</button>
        <button type="button" disabled={saving} onClick={onClose} className="h-10 px-4 border border-velta-border rounded-md text-sm font-medium disabled:opacity-40">Annulla</button>
        {!store.is_main && (
          <button type="button" disabled={saving} onClick={makeMain} className="h-10 px-4 border border-velta-border rounded-md text-sm font-medium disabled:opacity-40">Imposta come principale</button>
        )}
      </div>
    </div>
  );
}

export default function Stores() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [addr, setAddr] = useState("");
  const [color, setColor] = useState("#EF4444");
  const { user } = useAuth();
  const canEdit = !!user?.impersonation;
  const { refreshStores } = useStore();
  const [editingId, setEditingId] = useState("");

  const load = () => api.get("/stores", { params: { include_closed: true } }).then((r) => {
    const list = Array.isArray(r.data) ? r.data : [];
    setItems(list);
    refreshStores?.();
  });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    try {
      await api.post("/stores", { name, address: addr, is_main: false, color });
      toast.success("Punto vendita aggiunto");
      setName(""); setAddr("");
      setColor("#EF4444");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Solo il titolare può creare i punti vendita"));
    }
  };

  const setActive = async (store, active) => {
    if (!canEdit) return;
    const verb = active ? "riaprire" : "chiudere";
    if (!window.confirm(active
      ? `Riaprire «${store.name}»? Torna in cassa e nel calendario.`
      : `Chiudere «${store.name}»? Non compare più in cassa. Vendite e appuntamenti restano.`)) return;
    try {
      await api.patch(`/stores/${store.id}`, { active });
      toast.success(active ? "Punto vendita riaperto" : "Punto vendita chiuso");
      load();
    } catch (err) {
      toast.error(formatApiError(err, `Impossibile ${verb} il punto vendita`));
    }
  };

  const remove = async (store) => {
    if (!canEdit) return;
    if (!window.confirm(`Cancellare «${store.name}»? Si può solo se non ha vendite né appuntamenti. L'operazione non si annulla.`)) return;
    try {
      await api.delete(`/stores/${store.id}`);
      toast.success("Punto vendita cancellato");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile cancellare il punto vendita"));
    }
  };

  const setStoreColor = async (sid, hex) => {
    if (!canEdit) return;
    try {
      await api.patch(`/stores/${sid}`, { color: hex });
      setItems((prev) => (Array.isArray(prev) ? prev : []).map((s) => (s.id === sid ? { ...s, color: hex } : s)));
      refreshStores?.();
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile salvare il colore"));
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Multi-negozio</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Punti vendita</h1>
      <p className="text-velta-mute mb-8">
        {canEdit
          ? "Puoi modificare ogni sede, anche la principale. Il cliente la vede dopo aver chiuso e riaperto la cassa."
          : "Elenco sedi. Le modifiche le fa Velta. Chiudi e riapri la cassa per vederle."}
      </p>

      {canEdit && (
        <form onSubmit={add} className="bg-white border border-velta-border rounded-md p-5 mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nome</label>
              <input data-testid="store-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Roma" className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
            </div>
            <div className="flex-1 w-full">
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Indirizzo</label>
              <input value={addr} onChange={(e) => setAddr(e.target.value)} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
            </div>
            <button data-testid="store-add-btn" className="h-12 px-5 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
              <Plus className="w-4 h-4" /> Aggiungi
            </button>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Colore calendario</label>
            <ColorDots value={color} onChange={setColor} />
          </div>
        </form>
      )}

      <div className="space-y-4">
        {(Array.isArray(items) ? items : []).map((s) => {
          const closed = s.active === false;
          return (
          <div key={s.id} className={`bg-white border border-velta-border rounded-md p-5 ${closed ? "opacity-70" : ""}`} data-testid={`store-row-${s.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="font-medium inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color || "#94A3B8" }} />
                {s.name}
                {s.is_main ? <span className="text-xs font-medium text-velta-success">Principale</span> : null}
                {closed ? <span className="text-xs font-medium text-velta-mute">Chiuso</span> : null}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-sm text-velta-mute">{s.address || "—"}</div>
                {canEdit && (
                  <>
                    <button
                      type="button"
                      data-testid={`store-edit-${s.id}`}
                      onClick={() => setEditingId((cur) => (cur === s.id ? "" : s.id))}
                      className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Modifica
                    </button>
                    <button
                      type="button"
                      data-testid={`store-toggle-${s.id}`}
                      onClick={() => setActive(s, closed)}
                      className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1"
                    >
                      {closed ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                      {closed ? "Riapri" : "Chiudi"}
                    </button>
                    {!s.is_main && (
                      <button
                        type="button"
                        data-testid={`store-delete-${s.id}`}
                        onClick={() => remove(s)}
                        className="text-xs font-medium text-velta-error hover:underline inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Elimina
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2">Colore calendario</div>
              <ColorDots value={(s.color || "").toUpperCase()} onChange={(hex) => setStoreColor(s.id, hex)} disabled={!canEdit} />
            </div>
            <StoreIdentity
              store={s}
              open={canEdit && editingId === s.id}
              onClose={() => setEditingId("")}
              onSaved={() => load()}
            />
            <CardPosEditor
              store={s}
              owner={canEdit}
              onSaved={(doc) => {
                setItems((prev) => (Array.isArray(prev) ? prev : []).map((x) => (x.id === doc.id ? { ...x, ...doc } : x)));
                refreshStores?.();
              }}
            />
            <div className="mt-2 text-xs text-velta-mute">
              {s.card_pos_enabled && s.card_pos_host
                ? `Carta in cassa: invio automatico a ${s.card_pos_host}:${s.card_pos_port || 1000}`
                : "Carta in cassa: manuale sul POS del negozio"}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
